
import { Node } from '@xyflow/react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getAccountOwnershipInfo } from "./accountService";
import { fetchKpiData, processKpiData } from "./nodeProcessing";
import { initialNodes } from "./utils";
import React from 'react';

// Simple KPI creation service that deletes existing KPI records and creates new ones
export const createKpiRecordsFromCustomerData = async (
  companyId: string,
  setNodes: (updater: (nodes: Node[]) => Node[]) => void,
  updateRectangleWidths: (nodes: Node[]) => void,
  handleNodeDoubleClick: (event: React.MouseEvent, nodeId: string) => void
): Promise<boolean> => {
  try {
    console.log("Starting KPI creation from customer data for company:", companyId);
    
    // Get account ownership info
    const ownershipInfo = await getAccountOwnershipInfo(companyId);
    if (!ownershipInfo.canView) {
      console.log("User does not have permission to view this account");
      return false;
    }

    const accountOwnerUserId = ownershipInfo.accountOwnerUserId;
    if (!accountOwnerUserId) {
      console.log("No account owner found");
      return false;
    }

    // Only the account owner can create/update records
    if (!ownershipInfo.isOwner) {
      console.log("User is not the owner, cannot create KPI records");
      return false;
    }

    // Fetch KPI data from customer_kpis table
    const kpiData = await fetchKpiData(companyId, accountOwnerUserId);
    if (!kpiData) {
      console.log("No KPI data found to create records from");
      return false;
    }

    console.log("Found KPI data, starting delete and recreate process");

    // Step 1: Delete ONLY the specific KPI types (preserve Potential Solutions)
    const { error: deleteError } = await supabase
      .from('business_value_framework')
      .delete()
      .eq('company_id', companyId)
      .eq('user_id', accountOwnerUserId)
      .in('kpi_type', ['Executive KPIs', 'Core Financial KPIs', 'Business Initiatives', 'Operating KPIs']);

    if (deleteError) {
      console.error('Error deleting existing KPI records:', deleteError);
      toast.error("Failed to clear existing KPI records");
      return false;
    }

    console.log('Successfully deleted existing KPI records (preserved Potential Solutions)');

    // Step 2: Process KPI data and create new nodes
    const kpiNodes = processKpiData(kpiData, 1000, handleNodeDoubleClick);
    if (kpiNodes.length === 0) {
      console.log("No KPI nodes generated from data");
      return false;
    }

    console.log('Generated KPI nodes:', kpiNodes.length);

    // Step 3: Insert new KPI records with correct kpi_type values
    const kpiRecords = kpiNodes.map(node => {
      let kpiType = '';
      const idParts = node.id.match(/kpi-(\w+)-(\d+)/);
      
      if (idParts && idParts[1]) {
        switch(idParts[1]) {
          case 'exec': kpiType = 'Executive KPIs'; break;
          case 'financial': kpiType = 'Core Financial KPIs'; break;
          case 'initiative': kpiType = 'Business Initiatives'; break;
          case 'operating': kpiType = 'Operating KPIs'; break;
          default: kpiType = 'Potential Solutions'; // Keep full name, not just "solutions"
        }
      }

      return {
        company_id: companyId,
        title: String(node.data.title || ''),
        description: String(node.data.description || ''),
        position_x: node.position.x,
        position_y: node.position.y,
        kpi_type: kpiType,
        user_id: accountOwnerUserId
      };
    });

    const { error: insertError } = await supabase
      .from('business_value_framework')
      .insert(kpiRecords);

    if (insertError) {
      console.error('Error inserting new KPI records:', insertError);
      toast.error("Failed to create new KPI records");
      return false;
    }

    console.log('Successfully created new KPI records');

    // Step 4: Load ALL framework data (including preserved Potential Solutions)
    const { data: allFrameworkData, error: fetchError } = await supabase
      .from('business_value_framework')
      .select('*')
      .eq('company_id', companyId)
      .eq('user_id', accountOwnerUserId);

    if (fetchError) {
      console.error('Error fetching updated framework data:', fetchError);
      return false;
    }

    // Step 5: Create nodes from ALL framework data
    const frameworkNodes = allFrameworkData
      ?.filter(item => item.title && item.title.trim() !== '')
      .map(item => {
        // Standardized ID generation logic
        let nodeId;
        if (item.kpi_type && ['Executive KPIs', 'Core Financial KPIs', 'Business Initiatives', 'Operating KPIs'].includes(item.kpi_type)) {
          const typePrefix = item.kpi_type.toLowerCase().replace(/\s+/g, '-').replace('kpis', '').replace('initiatives', 'initiative');
          nodeId = `kpi-${typePrefix}-${item.id}`;
        } else {
          nodeId = `node-${item.id}`;
        }

        return {
          id: nodeId,
          type: 'default',
          data: {
            label: item.kpi_type ? {
              type: 'node-label',
              title: item.title,
              description: item.description || ''
            } : item.title,
            title: item.title,
            description: item.description || '',
            kpiType: item.kpi_type,
            onNodeDoubleClick: handleNodeDoubleClick,
            companyId: companyId
          },
          position: {
            x: item.position_x || 0,
            y: item.position_y || 0
          },
          style: {
            width: 200,
            height: 80,
            minWidth: 200,
            maxWidth: 200,
            minHeight: 80,
            maxHeight: 80,
            backgroundColor: 'white',
            padding: '8px',
            borderRadius: '8px'
          }
        };
      }) || [];

    // Step 6: Combine with initial nodes and update the flow
    const allNodes = [...initialNodes, ...frameworkNodes].map(node => ({
      ...node,
      data: {
        ...node.data,
        onNodeDoubleClick: handleNodeDoubleClick,
        companyId: companyId
      }
    }));

    setNodes(() => allNodes);
    updateRectangleWidths(allNodes);

    console.log(`Successfully created KPI records. Total nodes: ${allNodes.length}`);
    toast.success("KPI records created from customer data");
    return true;

  } catch (error) {
    console.error('Error in createKpiRecordsFromCustomerData:', error);
    toast.error("Failed to create KPI records from customer data");
    return false;
  }
};
